import React, { useCallback, useEffect, useState } from 'react';
import Header from '../../components/Header';
import MartyrSelect from '../../components/MartyrSelect';
import { writingsApi, WRITING_TYPE_LABELS } from '../../services/api';

const emptyForm = {
  martyr_id: '',
  title: '',
  author: '',
  writing_type: 'article',
  content: '',
  source: ''
};

const AdminWritingsPage = () => {
  const token = localStorage.getItem('adminToken');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [cover, setCover] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const data = await writingsApi.getAll({ limit: 50 }, token);
      setItems(data.writings || []);
    } catch (err) {
      setError(err.message || 'تعذر تحميل الكتابات');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      window.location.href = '/admin/login';
      return;
    }
    fetchItems();
  }, [token, fetchItems]);

  const resetForm = () => {
    setForm(emptyForm);
    setFile(null);
    setCover(null);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => formData.append(key, value));
      if (file) formData.append('file', file);
      if (cover) formData.append('cover', cover);

      if (editingId) {
        await writingsApi.update(editingId, formData, token);
        setSuccess('تم تحديث الكتابة بنجاح');
      } else {
        await writingsApi.create(formData, token);
        setSuccess('تمت إضافة الكتابة بنجاح');
      }
      resetForm();
      fetchItems();
    } catch (err) {
      setError(err.message || 'تعذر حفظ الكتابة');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      martyr_id: item.martyr_id ? String(item.martyr_id) : '',
      title: item.title || '',
      author: item.author || '',
      writing_type: item.writing_type || 'article',
      content: item.content || '',
      source: item.source || ''
    });
    setFile(null);
    setCover(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل تريد حذف هذه الكتابة؟')) return;
    try {
      await writingsApi.delete(id, token);
      fetchItems();
    } catch (err) {
      setError(err.message || 'تعذر الحذف');
    }
  };

  return (
    <div className="min-h-screen bg-white" dir="rtl">
      <Header />
      <main className="section-padding">
        <div className="container-responsive max-w-5xl">
          <h1 className="text-responsive-3xl font-bold text-primary-800 mb-2">كتب عنه</h1>
          <p className="text-gray-600 mb-8">اترك اسم الشهيد فارغاً للكتابات العامة في القائمة الرئيسية، أو اختر شهيداً لتظهر في زر «كتب عنه» داخل بطاقته.</p>

          {error && <div className="mb-4 p-4 rounded-lg bg-red-50 text-red-700">{error}</div>}
          {success && <div className="mb-4 p-4 rounded-lg bg-green-50 text-green-700">{success}</div>}

          <div className="card mb-8">
            <div className="card-body space-y-4">
              <h2 className="text-xl font-bold text-primary-800">
                {editingId ? 'تعديل الكتابة' : 'إضافة كتابة جديدة'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="form-group">
                  <label className="form-label">اسم الشهيد (اختياري)</label>
                  <MartyrSelect
                    value={form.martyr_id}
                    onChange={(value) => setForm((prev) => ({ ...prev, martyr_id: value }))}
                    token={token}
                    required={false}
                    emptyLabel="كتابة عامة — تظهر في صفحة كتب عنه"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">العنوان</label>
                    <input
                      className="form-input"
                      value={form.title}
                      onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">اسم الكاتب</label>
                    <input
                      className="form-input"
                      value={form.author}
                      onChange={(e) => setForm((prev) => ({ ...prev, author: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">النوع</label>
                    <select
                      className="form-select"
                      value={form.writing_type}
                      onChange={(e) => setForm((prev) => ({ ...prev, writing_type: e.target.value }))}
                    >
                      {Object.entries(WRITING_TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">المصدر (اختياري)</label>
                    <input
                      className="form-input"
                      value={form.source}
                      onChange={(e) => setForm((prev) => ({ ...prev, source: e.target.value }))}
                      placeholder="دار نشر، صحيفة، موقع..."
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">النص</label>
                  <textarea
                    className="form-input min-h-[180px]"
                    value={form.content}
                    onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
                    placeholder="أدخل نص الكتاب أو المقال هنا..."
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">ملف PDF أو مستند (اختياري)</label>
                    <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setFile(e.target.files[0] || null)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">غلاف أو صورة (اختياري)</label>
                    <input type="file" accept="image/*" onChange={(e) => setCover(e.target.files[0] || null)} />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'جاري الحفظ...' : editingId ? 'تحديث' : 'إضافة'}
                  </button>
                  {editingId && (
                    <button type="button" className="btn btn-secondary" onClick={resetForm}>إلغاء</button>
                  )}
                </div>
              </form>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h2 className="text-xl font-bold text-primary-800 mb-4">الكتابات المضافة</h2>
              {loading ? (
                <p>جاري التحميل...</p>
              ) : items.length === 0 ? (
                <p className="text-gray-500">لا توجد كتابات بعد</p>
              ) : (
                <div className="space-y-4">
                  {items.map((item) => (
                    <div key={item.id} className="border border-gray-200 rounded-lg p-4 flex justify-between gap-4">
                      <div>
                        <h3 className="font-bold text-lg">{item.title}</h3>
                        <p className="text-sm text-gray-600">
                          {WRITING_TYPE_LABELS[item.writing_type] || item.writing_type} — {item.author}
                        </p>
                        <p className="text-sm text-primary-700 mt-1">
                          {item.martyr_name_ar ? `عن الشهيد: ${item.martyr_name_ar}` : 'كتابة عامة'}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button className="btn btn-ghost btn-sm" onClick={() => handleEdit(item)}>تعديل</button>
                        <button className="btn btn-error btn-sm" onClick={() => handleDelete(item.id)}>حذف</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminWritingsPage;
